import {Filter} from "../../entities/shop/filter/filter";
import {ListResponse} from "../../entities/shop/product/listResponse";
import {Product} from "../../entities/shop/product/product";
import {Receipt} from "../../entities/shop/order/receipt";
import {Order} from "../../entities/shop/order/order";
import {shop_repository} from "../../../infrastructure/repository/impl/shop/shop_repository";

interface ShopUseCases {
  /** Listing of products with filters.
   * @param page current page referring to the product listing.
   * @param filter entity with user-selected filters.
   * @see Filter */
  filterProducts(page: number, filter: Filter): Promise<ListResponse<Product>>;

  /** Listagem dos produtos sem filtros. */
  products(): Promise<ListResponse<Product>>;

  /** Request to list filter entities.
   * @param filter is passed as a parameter to the filter in order to update the value of the products of each entity.
   * @see Filter */
  listFilters(filter: Filter): Promise<Filter>;

  /** Request to register a product in the favorites list.
   * @param id id of the product to be registered.
   * @see id */
  addToFavorite(id: number): Promise<void>;

  /** Request to list the products registered in the favorites list.
   * @param page parameter referring to the pagination of the product listing. Current page the user is on in the listing.
   * @param limit number of products per page.
   * @see page
   * @see limit */
  listFavoriteProducts(
    page?: number,
    limit?: number
  ): Promise<ListResponse<Product>>;

  /** Request to remove a product from the favorites list.
   * @param id id of the product to be removed.
   * @see id */
  removeProductFromFavorites(id: number);

  /** Request to get the information of a product.
   * @param id product id.
   * @see id */
  getProduct(id: number): Promise<Product>;

  /** Request used to finalize the user's purchase.
   * The return of this request will be the purchase receipt.
   * @param order object containing the order information.
   * @see Order
   * @see Receipt */
  order(order: Order): Promise<Receipt>;

  /** Request that returns the user's receipt when the purchase is completed.
   * @param orderID referring to the order id at checkout. */
  getReceipt(orderID: number): Promise<Receipt>;

  /** Request that returns the list of orders referring to the user.
   * This information comes from the user's cookie, it is not necessary to pass the user's id as a parameter.
   * @see Receipt */
  orders(): Promise<Receipt[]>;

  /** Request to create user receipt from order id.
   * @param orderID id of order. */
  createReceipt(orderID: number): Promise<Receipt>;

  /** Request to get all the products that are in the bag.
   * @see Product */
  getProductsFromBag(): Product[];

  /** Request to put a product in the bag.
   * @param product product to be placed in the bag.
   * @see Product */
  setProductToBag(product: Product): void;

  /** Request to put more than one product in the bag.
   * @param products array of products.
   * @see Product */
  setProductsToBag(products: Product[]): void;

  /** Request to remove a product from the bag.
   * @param product product to remove. */
  removeBagProduct(product: Product): void;

  /** Request to remove all products from the bag. Used when completing a purchase. */
  removeAllBagProducts(): void;
}

class _ShopUseCases implements ShopUseCases {
  async filterProducts(
    page: number,
    filter: Filter
  ): Promise<ListResponse<Product>> {
    return await shop_repository.filterProducts(filter, page);
  }

  async products(): Promise<ListResponse<Product>> {
    return await shop_repository.products();
  }

  async listFilters(filter: Filter): Promise<Filter> {
    return await shop_repository.listFilters(filter);
  }

  async addToFavorite(id: number): Promise<void> {
    return await shop_repository.addToFavorite(id);
  }

  async listFavoriteProducts(
    page?: number,
    limit?: number
  ): Promise<ListResponse<Product>> {
    return await shop_repository.listFavoriteProducts(page, limit);
  }

  async removeProductFromFavorites(id: number) {
    return await shop_repository.removeProductFromFavorites(id);
  }

  async getProduct(id: number): Promise<Product> {
    return await shop_repository.getProduct(id);
  }

  async order(order: Order): Promise<Receipt> {
    return await shop_repository.order(order);
  }

  async getReceipt(orderID: number): Promise<Receipt> {
    return await shop_repository.getReceipt(orderID);
  }

  async orders(): Promise<Receipt[]> {
    return await shop_repository.orders();
  }

  async createReceipt(orderID: number): Promise<Receipt> {
    return await shop_repository.createReceipt(orderID);
  }

  getProductsFromBag(): Product[] {
    return shop_repository.getProductsFromBag();
  }

  setProductToBag(product: Product): void {
    return shop_repository.setProductToBag(product);
  }

  setProductsToBag(products: Product[]): void {
    return shop_repository.setBagProducts(products);
  }

  removeBagProduct(product: Product): void {
    return shop_repository.removeBagProduct(product);
  }

  removeAllBagProducts(): void {
    return shop_repository.removeAllBagProducts();
  }
}

export default new _ShopUseCases() as ShopUseCases;
